/**
 * Digital Ads Asset Creation Component
 * Handles Google Ads, Facebook/Instagram Ads, LinkedIn Ads, Display Ads
 */

import React, { useState } from 'react';
import { Search, Image as ImageIcon, Linkedin, Monitor, Upload, Eye, Send, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://content-review-api.ryantoth2410.workers.dev/api';

interface AdsAssetProps {
  clientId?: number;
  planId?: number;
  projectId?: number;
  onComplete?: (contentId: number) => void;
  onCancel?: () => void;
}

interface AdPlatform {
  id: string;
  name: string;
  icon: React.ReactNode;
  adTypes: string[];
  color: string;
}

const adPlatforms: AdPlatform[] = [
  { id: 'google_ads', name: 'Google Ads', icon: <Search className="w-5 h-5" />, adTypes: ['Search', 'Display', 'Shopping', 'Video', 'Performance Max'], color: 'bg-blue-500' },
  { id: 'facebook_ads', name: 'Facebook/Instagram', icon: <ImageIcon className="w-5 h-5" />, adTypes: ['Feed', 'Story', 'Carousel', 'Video', 'Collection'], color: 'bg-blue-600' },
  { id: 'linkedin_ads', name: 'LinkedIn Ads', icon: <Linkedin className="w-5 h-5" />, adTypes: ['Sponsored Content', 'Message Ads', 'Text Ads', 'Dynamic Ads'], color: 'bg-blue-700' },
  { id: 'display', name: 'Display Network', icon: <Monitor className="w-5 h-5" />, adTypes: ['Banner', 'Responsive', 'HTML5', 'Native'], color: 'bg-purple-500' },
];

interface AdVariation {
  id: string;
  headline: string;
  description: string;
  callToAction: string;
}

export default function AdsAssetCreation({ clientId, planId, projectId, onComplete, onCancel }: AdsAssetProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlatform, setSelectedPlatform] = useState<AdPlatform | null>(null);
  const [adType, setAdType] = useState('');
  const [creatives, setCreatives] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);

  const [campaignData, setCampaignData] = useState({
    campaignName: '',
    campaignObjective: '',
    budget: '',
    budgetType: 'daily',
    startDate: '',
    endDate: '',
  });

  const [adVariations, setAdVariations] = useState<AdVariation[]>([
    { id: '1', headline: '', description: '', callToAction: '' }
  ]);

  const [targetingData, setTargetingData] = useState({
    locations: '',
    ageRange: '',
    gender: '',
    interests: '',
    keywords: '',
    placements: '',
  });

  const addVariation = () => {
    setAdVariations([
      ...adVariations,
      { id: Date.now().toString(), headline: '', description: '', callToAction: '' }
    ]);
  };

  const removeVariation = (id: string) => {
    if (adVariations.length > 1) {
      setAdVariations(adVariations.filter(v => v.id !== id));
    }
  };

  const updateVariation = (id: string, field: keyof AdVariation, value: string) => {
    setAdVariations(adVariations.map(v =>
      v.id === id ? { ...v, [field]: value } : v
    ));
  };

  const handleCreativesUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setCreatives(Array.from(e.target.files));
    }
  };

  const handleSubmit = async () => {
    if (!selectedPlatform) return;

    setLoading(true);

    try {
      // Step 1: Create content item
      const contentPayload = {
        type: 'ads',
        content_subtype: `${selectedPlatform.id}_${adType.toLowerCase().replace(/\s+/g, '_')}`,
        title: campaignData.campaignName,
        description: `${selectedPlatform.name} - ${adType} campaign`,
        status: 'draft',
        client_id: clientId,
        plan_id: planId,
        project_id: projectId,
        platform_config: JSON.stringify({
          platform: selectedPlatform.id,
          ad_type: adType,
          campaign: campaignData,
          variations: adVariations,
          targeting: targetingData,
        }),
      };

      const contentRes = await axios.post(`${API_BASE_URL}/content`, contentPayload);
      const contentId = contentRes.data.id;

      // Step 2: Upload creative assets
      if (creatives.length > 0) {
        for (const [index, file] of creatives.entries()) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('content_id', contentId.toString());
          formData.append('asset_type', file.type.startsWith('video/') ? 'video' : 'image');
          formData.append('asset_group', 'primary');
          formData.append('platform_target', selectedPlatform.id);
          formData.append('variant_name', `creative_${index + 1}`);

          await axios.post(`${API_BASE_URL}/assets/upload`, formData);
        }
      }

      // Step 3: Create workflow steps
      const workflowSteps = [
        { step_order: 1, step_name: 'Platform & Type Selection', step_type: 'metadata', status: 'completed' },
        { step_order: 2, step_name: 'Campaign Setup', step_type: 'metadata', status: 'completed' },
        { step_order: 3, step_name: 'Ad Copy Variations', step_type: 'content', status: 'completed' },
        { step_order: 4, step_name: 'Creative Upload', step_type: 'assets', status: 'completed' },
        { step_order: 5, step_name: 'Submit for Approval', step_type: 'approval', status: 'pending' },
      ];

      for (const step of workflowSteps) {
        await axios.post(`${API_BASE_URL}/content/${contentId}/workflow/steps`, step);
      }

      alert('Ad campaign created successfully!');
      if (onComplete) onComplete(contentId);
    } catch (error) {
      console.error('Failed to create ads asset:', error);
      alert('Failed to create ad campaign');
    } finally {
      setLoading(false);
    }
  };

  const getCharacterLimits = () => {
    if (!selectedPlatform) return { headline: 0, description: 0 };

    switch (selectedPlatform.id) {
      case 'google_ads':
        return { headline: 30, description: 90 };
      case 'facebook_ads':
        return { headline: 40, description: 125 };
      case 'linkedin_ads':
        return { headline: 70, description: 150 };
      case 'display':
        return { headline: 50, description: 100 };
      default:
        return { headline: 0, description: 0 };
    }
  };

  const limits = getCharacterLimits();

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Digital Ad Campaign</h1>
              <p className="text-gray-600 mt-1">Multi-platform paid advertising campaigns</p>
            </div>
            {onCancel && (
              <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                Cancel
              </button>
            )}
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mt-6">
            {[
              { num: 1, label: 'Platform' },
              { num: 2, label: 'Campaign' },
              { num: 3, label: 'Ad Copy' },
              { num: 4, label: 'Creatives' },
              { num: 5, label: 'Preview' },
            ].map((step, i) => (
              <React.Fragment key={step.num}>
                <div className={`flex items-center ${currentStep >= step.num ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    currentStep >= step.num ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    {step.num}
                  </div>
                  <span className="ml-2 text-sm font-medium hidden sm:inline">{step.label}</span>
                </div>
                {i < 4 && <ChevronRight className="w-4 h-4 text-gray-400 mx-2" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {/* Step 1: Platform & Ad Type Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Select Advertising Platform</h2>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {adPlatforms.map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => setSelectedPlatform(platform)}
                    className={`p-4 rounded-lg border-2 transition ${
                      selectedPlatform?.id === platform.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className={`w-12 h-12 ${platform.color} rounded-lg flex items-center justify-center mx-auto mb-2 text-white`}>
                      {platform.icon}
                    </div>
                    <p className="text-sm font-medium text-center">{platform.name}</p>
                  </button>
                ))}
              </div>

              {selectedPlatform && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ad Type</label>
                  <select
                    value={adType}
                    onChange={(e) => setAdType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select ad type...</option>
                    {selectedPlatform.adTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setCurrentStep(2)}
                  disabled={!selectedPlatform || !adType}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  Next: Campaign Setup
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Campaign Setup */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Campaign Configuration</h2>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name *</label>
                <input
                  type="text"
                  value={campaignData.campaignName}
                  onChange={(e) => setCampaignData({ ...campaignData, campaignName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Q4 Product Launch - Google Search"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Objective</label>
                <select
                  value={campaignData.campaignObjective}
                  onChange={(e) => setCampaignData({ ...campaignData, campaignObjective: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select objective...</option>
                  <option value="awareness">Brand Awareness</option>
                  <option value="consideration">Consideration</option>
                  <option value="conversions">Conversions</option>
                  <option value="traffic">Traffic</option>
                  <option value="leads">Lead Generation</option>
                  <option value="sales">Sales</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={campaignData.budget}
                      onChange={(e) => setCampaignData({ ...campaignData, budget: e.target.value })}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      placeholder="Amount"
                    />
                    <select
                      value={campaignData.budgetType}
                      onChange={(e) => setCampaignData({ ...campaignData, budgetType: e.target.value })}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="daily">Daily</option>
                      <option value="lifetime">Lifetime</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={campaignData.startDate}
                      onChange={(e) => setCampaignData({ ...campaignData, startDate: e.target.value })}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="date"
                      value={campaignData.endDate}
                      onChange={(e) => setCampaignData({ ...campaignData, endDate: e.target.value })}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold text-purple-900 mb-3">Targeting (Optional)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={targetingData.locations}
                    onChange={(e) => setTargetingData({ ...targetingData, locations: e.target.value })}
                    className="px-4 py-2 border border-purple-200 rounded-lg"
                    placeholder="Locations"
                  />
                  <input
                    type="text"
                    value={targetingData.ageRange}
                    onChange={(e) => setTargetingData({ ...targetingData, ageRange: e.target.value })}
                    className="px-4 py-2 border border-purple-200 rounded-lg"
                    placeholder="Age Range (e.g., 25-45)"
                  />
                  <input
                    type="text"
                    value={targetingData.interests}
                    onChange={(e) => setTargetingData({ ...targetingData, interests: e.target.value })}
                    className="px-4 py-2 border border-purple-200 rounded-lg"
                    placeholder="Interests"
                  />
                  <input
                    type="text"
                    value={targetingData.keywords}
                    onChange={(e) => setTargetingData({ ...targetingData, keywords: e.target.value })}
                    className="px-4 py-2 border border-purple-200 rounded-lg"
                    placeholder="Keywords"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(1)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(3)}
                  disabled={!campaignData.campaignName}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Ad Copy
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Ad Copy Variations */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Ad Copy Variations (A/B Testing)</h2>
                <button
                  onClick={addVariation}
                  className="px-4 py-2 border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Variation
                </button>
              </div>

              <div className="space-y-4">
                {adVariations.map((variation, index) => (
                  <div key={variation.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium text-gray-900">Variation {index + 1}</h3>
                      {adVariations.length > 1 && (
                        <button
                          onClick={() => removeVariation(variation.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Headline</label>
                        <input
                          type="text"
                          value={variation.headline}
                          onChange={(e) => updateVariation(variation.id, 'headline', e.target.value)}
                          maxLength={limits.headline}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Compelling headline"
                        />
                        <p className="text-xs text-gray-500 mt-1">{variation.headline.length}/{limits.headline} characters</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={variation.description}
                          onChange={(e) => updateVariation(variation.id, 'description', e.target.value)}
                          maxLength={limits.description}
                          rows={3}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Compelling description"
                        />
                        <p className="text-xs text-gray-500 mt-1">{variation.description.length}/{limits.description} characters</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Call to Action</label>
                        <select
                          value={variation.callToAction}
                          onChange={(e) => updateVariation(variation.id, 'callToAction', e.target.value)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select CTA...</option>
                          <option value="learn_more">Learn More</option>
                          <option value="shop_now">Shop Now</option>
                          <option value="sign_up">Sign Up</option>
                          <option value="get_quote">Get Quote</option>
                          <option value="download">Download</option>
                          <option value="contact_us">Contact Us</option>
                          <option value="apply_now">Apply Now</option>
                          <option value="book_now">Book Now</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(2)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(4)}
                  disabled={adVariations.some(v => !v.headline || !v.description || !v.callToAction)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                >
                  Next: Creatives
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Creative Upload */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Upload Ad Creatives</h2>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8">
                <div className="text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <label htmlFor="creatives-upload" className="cursor-pointer">
                    <span className="text-blue-600 hover:text-blue-700 font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-2">Images or videos (max 10MB each)</p>
                  <input
                    id="creatives-upload"
                    type="file"
                    onChange={handleCreativesUpload}
                    multiple
                    accept="image/*,video/*"
                    className="hidden"
                  />
                </div>

                {creatives.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {creatives.map((file, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {selectedPlatform?.id === 'google_ads' && adType === 'Display' && (
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-semibold text-blue-900 mb-2">Recommended Display Ad Sizes</h3>
                  <div className="grid grid-cols-3 gap-2 text-xs text-blue-700">
                    <div className="bg-white p-2 rounded">300x250 (Medium Rectangle)</div>
                    <div className="bg-white p-2 rounded">728x90 (Leaderboard)</div>
                    <div className="bg-white p-2 rounded">160x600 (Wide Skyscraper)</div>
                    <div className="bg-white p-2 rounded">300x600 (Half Page)</div>
                    <div className="bg-white p-2 rounded">320x50 (Mobile Banner)</div>
                    <div className="bg-white p-2 rounded">970x250 (Billboard)</div>
                  </div>
                </div>
              )}

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(3)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={() => setCurrentStep(5)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Eye className="w-4 h-4 inline mr-1" />
                  Preview
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Preview & Submit */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <h2 className="text-lg font-semibold">Preview & Submit</h2>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="mb-4">
                  <h3 className="font-semibold text-gray-900 mb-2">Campaign Summary</h3>
                  <div className="bg-white p-4 rounded-lg space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Platform:</span>
                      <span className="font-medium">{selectedPlatform?.name} - {adType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Campaign:</span>
                      <span className="font-medium">{campaignData.campaignName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Budget:</span>
                      <span className="font-medium">${campaignData.budget} {campaignData.budgetType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Variations:</span>
                      <span className="font-medium">{adVariations.length}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Creatives:</span>
                      <span className="font-medium">{creatives.length} file(s)</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Ad Variations Preview</h3>
                  <div className="space-y-3">
                    {adVariations.map((variation, index) => (
                      <div key={variation.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-gray-200 w-16 h-16 rounded flex-shrink-0"></div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm text-gray-900 mb-1">{variation.headline}</h4>
                            <p className="text-xs text-gray-600 mb-2">{variation.description}</p>
                            <button className="text-xs text-blue-600 font-medium px-3 py-1 border border-blue-600 rounded">
                              {variation.callToAction.replace(/_/g, ' ').toUpperCase()}
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-400">Variation {index + 1}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setCurrentStep(4)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  <ChevronLeft className="w-4 h-4 inline mr-1" />
                  Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Approval
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
